import { BN, MarketType, OrderActionExplanation } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UIMarket,
	UISerializableDepositRecord,
	UISerializableFundingPaymentRecord,
	UISerializableInsuranceFundStakeRecord,
	UISerializableOrderActionRecord,
	UISerializableOrderRecord,
	UISerializableOrderRecordV2,
	UISerializableSettlePnlRecord,
} from '@velocity-exchange/common';
import dayjs from 'dayjs';
import type { ParentOrderInfo } from 'src/@types/historyTables';
import {
	BaseFilterState,
	HistoryTableFilterStore,
} from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';

export type HistoryTableFilterContext = {
	isLive?: boolean;
};

export const convertV2OrderRecordToParentOrderInfo = (
	record: UISerializableOrderRecordV2
): ParentOrderInfo => {
	return {
		ts: record.ts,
		slot: record.slot,
		orderType: record.orderType,
		marketType: record.marketType,
		orderId: record.orderId,
		marketIndex: record.marketIndex,
		price: record.price,
		baseAssetAmount: record.baseAssetAmount,
		baseAssetAmountFilled: record.baseAssetAmountFilled,
		quoteAssetAmountFilled: record.quoteAssetAmountFilled,
		direction: record.direction,
		reduceOnly: record.reduceOnly,
		triggerPrice: record.triggerPrice,
		triggerCondition: record.triggerCondition,
		existingPositionDirection: record.existingPositionDirection,
		postOnly: record.postOnly,
		immediateOrCancel: record.immediateOrCancel,
		oraclePriceOffset: record.oraclePriceOffset,
		lastUpdatedTs: record.lastUpdatedTs,
		lastActionExplanation: record.lastActionExplanation,
		lastActionStatus: record.lastActionStatus,
		cumulativeFee: record.cumulativeFee,
	};
};

export const convertOrderRecordToParentOrderInfo = (
	record: UISerializableOrderRecord
): ParentOrderInfo => {
	return {
		ts: record.ts,
		slot: record.slot,
		orderId: record.order.orderId,
		marketIndex: record.order.marketIndex,
		marketType: record.order.marketType,
		orderType: record.order.orderType,
		direction: record.order.direction,
		price: record.order.price,
		baseAssetAmount: record.order.baseAssetAmount,
		baseAssetAmountFilled: record.order.baseAssetAmountFilled,
		quoteAssetAmountFilled: record.order.quoteAssetAmountFilled,
		reduceOnly: record.order.reduceOnly,
		triggerPrice: record.order.triggerPrice,
		triggerCondition: record.order.triggerCondition,
		existingPositionDirection: record.order.existingPositionDirection,
		postOnly: record.order.postOnly,
		immediateOrCancel: record.order.immediateOrCancel,
		oraclePriceOffset: record.order.oraclePriceOffset,
		lastUpdatedTs: record.ts,
		lastActionExplanation: OrderActionExplanation.NONE,
		lastActionStatus: ENUM_UTILS.toStr(record.order.status),
		cumulativeFee: null,
	};
};

const checkDateRange = (
	record: { ts: BN },
	filterState: BaseFilterState,
	context?: HistoryTableFilterContext
) => {
	const isLive = context?.isLive;

	if (isLive) {
		return true;
	} // Don't apply date range filters to the live data

	return (
		(filterState.startDate === null
			? true
			: dayjs.unix(record.ts.toNumber()).isAfter(filterState.startDate)) &&
		(filterState.endDate === null
			? true
			: dayjs.unix(record.ts.toNumber()).isBefore(filterState.endDate))
	);
};

export const getDepositHistoryFilter = (
	record: UISerializableDepositRecord,
	filterState: HistoryTableFilterStore['deposits']
) => {
	const matchesDirectionFilter =
		filterState.type === 'all' ||
		ENUM_UTILS.toStr(record.direction) === filterState.type;
	const matchesSpotMarketIndexFilter =
		filterState.spotMarketSymbol === 'all_markets' ||
		UIMarket.spotMarkets[record.marketIndex].symbol ===
			filterState.spotMarketSymbol;

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return (
		matchesDirectionFilter &&
		matchesSpotMarketIndexFilter &&
		matchesDateRangeFilter
	);
};

export const getRewardHistoryFilter = (
	record: UISerializableDepositRecord,
	filterState: HistoryTableFilterStore['rewards']
) => {
	const matchesSpotMarketIndexFilter =
		filterState.spotMarketSymbol === 'all_markets' ||
		UIMarket.spotMarkets[record.marketIndex].symbol ===
			filterState.spotMarketSymbol;

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return matchesSpotMarketIndexFilter && matchesDateRangeFilter;
};

export const getTradesHistoryFilter = (
	record: UISerializableOrderActionRecord,
	filterState: HistoryTableFilterStore['trades']
) => {
	const marketConfig = ENUM_UTILS.match(record.marketType, MarketType.PERP)
		? UIMarket.perpMarkets[record.marketIndex]
		: UIMarket.spotMarkets[record.marketIndex];
	const matchesMarketSymbolFilter =
		filterState.marketSymbol === 'all_markets' ||
		marketConfig.symbol === filterState.marketSymbol;

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return matchesMarketSymbolFilter && matchesDateRangeFilter;
};

export const orderHistoryFilter = (
	record: ParentOrderInfo,
	filterState: HistoryTableFilterStore['orders']
) => {
	const marketConfig = ENUM_UTILS.match(record.marketType, MarketType.PERP)
		? UIMarket.perpMarkets[record.marketIndex]
		: UIMarket.spotMarkets[record.marketIndex];
	const matchesMarketSymbolFilter =
		filterState.marketSymbol === 'all_markets' ||
		marketConfig.symbol === filterState.marketSymbol;

	const matchesFilledFilter =
		!filterState.showOnlyFilledOrders || !record.baseAssetAmountFilled.eqZero();

	return matchesMarketSymbolFilter && matchesFilledFilter;
};

export const getFundingHistoryFilter = (
	record: UISerializableFundingPaymentRecord,
	filterState: HistoryTableFilterStore['funding-payments']
) => {
	const marketConfig = UIMarket.perpMarkets[record.marketIndex];
	const matchesMarketSymbolFilter =
		filterState.marketSymbol === 'all_markets' ||
		marketConfig.symbol === filterState.marketSymbol;

	const matchesDirectionFilter =
		filterState.direction === 'all' ||
		(record.baseAssetAmount.isNeg() && filterState.direction === 'short') ||
		(record.baseAssetAmount.isPos() && filterState.direction === 'long');

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return (
		matchesMarketSymbolFilter &&
		matchesDirectionFilter &&
		matchesDateRangeFilter
	);
};

export const getIfStakingHistoryFilter = (
	record: UISerializableInsuranceFundStakeRecord,
	filterState: HistoryTableFilterStore['if-staking']
) => {
	const marketConfig = UIMarket.spotMarkets[record.marketIndex];
	const matchesMarketSymbolFilter =
		filterState.spotMarketSymbol === 'all_markets' ||
		marketConfig.symbol === filterState.spotMarketSymbol;

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return matchesMarketSymbolFilter && matchesDateRangeFilter;
};

export const getSettledBalancesHistoryFilter = (
	record: UISerializableSettlePnlRecord,
	filterState: HistoryTableFilterStore['settled-balances']
) => {
	const marketConfig = UIMarket.perpMarkets[record.marketIndex];
	const matchesMarketSymbolFilter =
		filterState.marketSymbol === 'all_markets' ||
		marketConfig.symbol === filterState.marketSymbol;

	const matchesDateRangeFilter = checkDateRange(record, filterState);

	return matchesMarketSymbolFilter && matchesDateRangeFilter;
};
